import { gql } from '@apollo/client';
import { Grid, GridItem, Heading } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  BurnInRefreshIcon,
  DateTimeIcon,
  GridIcon,
  LandscapeIcon,
  LockIcon,
  PlaylistIcon,
  PowerIcon,
  PowerScheduleIcon,
  VolumeHighIcon,
  WebPageIcon,
} from '~components/ui/icons';
import { useTabs } from '~components/ui/Tabs';
import {
  ActiveTabBackground,
  VerticalTab,
  VerticalTabList,
  VerticalTabPanel,
  VerticalTabs,
} from '~components/ui/VerticalTabs';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { useFeatureFlag } from '~utils/features';
import { ConfigurationStatusType } from '../ConfigStateToggle';
import {
  ConfigDetailSettings_ConfigFragment,
  ConfigDetailSettings_CustomerFragment,
} from './__generated__/ConfigDetailSettings.graphql';
import { ConfigApps } from './apps/ConfigApps';
import { ConfigBurnInRefreshSection } from './burnInRefresh/ConfigBurnInRefreshSection';
import { useConfigDetailSettingsTab } from './ConfigDetailView';
import { ConfigGeneralSection } from './general/ConfigGeneralSection';
import { ConfigDisplayLockingSection } from './locking/ConfigDisplayLockingSection';
import { ConfigPlaybackSection } from './playback/PlaybackSection';
import { ConfigPlaylistSelect } from './playlist/ConfigDisplayPlaylist';
import { ConfigPowerSettingsSection } from './power/ConfigPowerSettingsSection';
import { ConfigPowerScheduleSelect } from './schedule/ConfigDisplayPowerSchedule';
import { ConfigScheduleSelect } from './schedule/ConfigDisplaySchedule';
import { ConfigTimeSection } from './time/ConfigTimeSection';
import { ConfigWebPagesForm } from './webpages/ConfigWebPages';

function supportsBookmarks(config: ConfigDetailSettings_ConfigFragment): boolean {
  return !!config.bookmarks?.all?.reported?.length;
}

function supportsPlaylists(config: ConfigDetailSettings_ConfigFragment): boolean {
  return config.playlist != null;
}

function supportsConfigApps(config: ConfigDetailSettings_ConfigFragment): boolean {
  return config.configApps != null;
}

function supportsBurnInRefresh(config: ConfigDetailSettings_ConfigFragment): boolean {
  return config.burnInRefresh != null;
}

export function checkStateToDisableTab(configStatus?: ConfigurationStatusType): boolean {
  return (
    configStatus === ConfigurationStatus.Published ||
    configStatus === ConfigurationStatus.Readytopublish
  );
}
export function ConfigDetailSettingsPanel({
  scrollOffset,
  configuration,
  customer,
  configStatus,
}: {
  customer: ConfigDetailSettings_CustomerFragment;
  configuration: ConfigDetailSettings_ConfigFragment;
  scrollOffset?: number;
  configStatus?: ConfigurationStatusType;
}) {
  const { t } = useTranslation();
  const { isEnabled: isSchedulesEnabled } = useFeatureFlag('schedules');

  const tabDefinitions = [
    {
      label: t('general'),
      icon: LandscapeIcon,
      element: (
        <ConfigGeneralSection
          customer={customer}
          config={configuration}
          configStatus={configStatus}
        />
      ),
      path: '#general',
    },
    {
      label: t('dateAndTime'),
      icon: DateTimeIcon,
      element: <ConfigTimeSection config={configuration} configStatus={configStatus} />,
      path: '#datetime',
    },
    {
      label: t('playback'),
      icon: VolumeHighIcon,
      element: <ConfigPlaybackSection config={configuration} configStatus={configStatus} />,
      path: '#playback',
    },
    {
      label: t('power'),
      icon: PowerIcon,
      element: (
        <ConfigPowerSettingsSection
          config={configuration}
          isNotSupported={
            isNil(configuration?.signalDetection?.reported) &&
            isNil(configuration?.battery?.reported) &&
            isNil(configuration?.autoRestart?.reported)
          }
          configStatus={configStatus}
        />
      ),
      path: '#power',
    },
    ...(supportsBurnInRefresh(configuration)
      ? [
          {
            label: t('burnInRefresh'),
            icon: BurnInRefreshIcon,
            element: (
              <ConfigBurnInRefreshSection config={configuration} configStatus={configStatus} />
            ),
            path: '#burnInRefresh',
          },
        ]
      : []),
    {
      label: t('locking'),
      icon: LockIcon,
      element: <ConfigDisplayLockingSection config={configuration} configStatus={configStatus} />,
      path: '#locking',
    },
    {
      label: t('schedules'),
      icon: PowerScheduleIcon,
      element: isSchedulesEnabled ? (
        <ConfigScheduleSelect
          customer={customer}
          config={configuration}
          configStatus={configStatus}
        />
      ) : (
        <ConfigPowerScheduleSelect
          customer={customer}
          config={configuration}
          configStatus={configStatus}
        />
      ),
      path: '#schedules',
    },
    ...(supportsPlaylists(configuration)
      ? [
          {
            label: t('playlist'),
            icon: PlaylistIcon,
            element: (
              <ConfigPlaylistSelect
                customer={customer}
                config={configuration}
                configStatus={configStatus}
              />
            ),
            path: '#playlist',
          },
        ]
      : []),
    ...(supportsConfigApps(configuration)
      ? [
          {
            label: t('apps'),
            icon: GridIcon,
            element: <ConfigApps configuration={configuration} configStatus={configStatus} />,
            path: '#apps',
          },
        ]
      : []),
    ...(supportsBookmarks(configuration)
      ? [
          {
            label: t('webPages'),
            icon: WebPageIcon,
            element: (
              <ConfigWebPagesForm
                configurationId={configuration.id}
                defaultBookmarks={configuration.bookmarks.all.reported || []}
                configStatus={configStatus}
              />
            ),
            path: '#webpages',
          },
        ]
      : []),
  ];
  const { tabs } = useTabs(tabDefinitions, { animateVertical: true, scrollOffset });
  return (
    <>
      <VerticalTabs spacing={{ base: 0, lg: 10 }}>
        <VerticalTabList
          stickyOffset={scrollOffset}
          display={{ base: 'none', lg: 'block' }}
          style={{ marginRight: '110px' }}
        >
          {tabs.map((tab, i) => (
            <VerticalTab key={tab.path} ref={tab.ref} tab={tab} prevTab={tabs[i - 1]} />
          ))}
          <ActiveTabBackground tabs={tabs} />
        </VerticalTabList>
        <VerticalTabPanel>
          {tabs.map(({ icon: Icon, ...tab }) => (
            <Grid
              key={tab.path}
              templateColumns="repeat(8, 1fr)"
              gap="0 5%"
              mt="120"
              _first={{ mt: 0 }}
              ref={tab.contentRef}
            >
              <GridItem colSpan={7}>
                <Heading
                  id={tab.path?.replace('#', '')}
                  borderBottom="1px solid"
                  borderColor={tab.borderColor || 'gray.50'}
                  fontSize="1.5rem"
                  mb="8"
                  pb="2"
                  display="flex"
                  alignItems="center"
                  color={tab.color}
                >
                  {Icon !== undefined && <Icon width="6" height="6" mr="3" />}
                  {tab.label}
                </Heading>
              </GridItem>
              {tab.element}
            </Grid>
          ))}
        </VerticalTabPanel>
      </VerticalTabs>
    </>
  );
}

export function ConfigDetailSettings() {
  const { customer, scrollOffset, configuration, configStatus } = useConfigDetailSettingsTab();
  return (
    <ConfigDetailSettingsPanel
      customer={customer}
      scrollOffset={scrollOffset}
      configuration={configuration}
      configStatus={configStatus}
    />
  );
}

ConfigDetailSettings.graphql = {
  fragments: {
    ConfigDetailSettings_customer: gql`
      fragment ConfigDetailSettings_customer on Customer {
        id
        ...ConfigGeneralSection_customer
        ...ConfigScheduleSelect_customer
        ...ConfigPowerScheduleSelect_customer
        ...ConfigPlaylistSelect_customer
      }
    `,
    ConfigDetailSettings_config: gql`
      fragment ConfigDetailSettings_config on ConfigurationModel {
        id
        supportedFirmwares
        ...ConfigGeneralSection_config
        ...ConfigTimeSection_config
        ...ConfigPowerSettingsSection_config
        ...ConfigDisplayAutoRestart_config
        ...ConfigDisplayLockingSection_config
        ...ConfigScheduleSelect_config
        ...ConfigPowerScheduleSelect_config
        ...ConfigPlaylistSelect_config
        ...ConfigPlaybackSection_config
        ...ConfigFirmwareVersionSelect_config
        ...ConfigApps_config
        ...ConfigBookmarks_config
        ...ConfigSummary_config
        ...ConfigBurnInRefreshSection_config
      }
    `,
  },
};
